import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Timeline } from '@mui/lab';
import { Alert, Box, Collapse, Fade, Stack, styled, Typography } from '@mui/material';

import { Button, Loading, PageContentPadding } from '@components/base';
import { useProviderNames } from '@hooks/useProviderNames';
import { vodProviderIdentifier } from '@models/Provider';
import { DetailedPlaybackOption } from '@models/Title';

import useRawDataRevision from '../hooks/useRawDataRevision';

import CompareRevisions from './CompareRevisions';
import RawMetadataRevision from './RawMetadataRevision';

const classes = {
  clearHighlightButton: 'clear-highlight',
};

const StickyHeadingWrapper = styled(Stack)`
  align-items: center;
  background: ${({ theme }) => theme.palette.background.default};
  border-bottom: thin solid ${({ theme }) => theme.palette.divider};
  min-height: 60px;
  position: sticky;
  top: 0;
  z-index: 2;

  .${classes.clearHighlightButton} {
    width: max-content;
  }
`;

type RawMetadataRevisionsProps = {
  playbackOption: DetailedPlaybackOption;
};

const RawMetadataRevisions: FC<RawMetadataRevisionsProps> = ({
  playbackOption: { providerIdentifier, id, sourceId },
}) => {
  const { getProviderName } = useProviderNames();
  const { t } = useTranslation();

  const {
    hasRepeatedMetadata,
    highlightedHash,
    isLoading,
    queryResult: { error, isError },
    revisions,
    setHighlightedHash,
  } = useRawDataRevision();

  return (
    <Stack>
      <StickyHeadingWrapper direction='row' px={PageContentPadding} py={1}>
        <Stack flex={1}>
          <Typography variant='h3'>{getProviderName(providerIdentifier)}</Typography>

          {providerIdentifier === vodProviderIdentifier && (
            <Typography color='textSecondary' variant='subtitle2'>
              {sourceId}
            </Typography>
          )}

          {revisions && !isLoading && (
            <Typography color='textSecondary' variant='caption'>
              {t('rawMetadata.revisions', { count: revisions.length })}
            </Typography>
          )}
        </Stack>

        <Stack alignItems='center' direction='row' spacing={1}>
          <CompareRevisions />

          <Collapse in={!!(hasRepeatedMetadata && highlightedHash)} orientation='horizontal'>
            <Button
              className={classes.clearHighlightButton}
              onClick={e => {
                e.stopPropagation();
                setHighlightedHash(undefined);
              }}
              variant='contained'
            >
              {t('clearHighlight')}
            </Button>
          </Collapse>
        </Stack>
      </StickyHeadingWrapper>

      {isError ? (
        <Box p={PageContentPadding}>
          <Alert severity='error'>{error.message}</Alert>
        </Box>
      ) : isLoading ? (
        <Loading BoxProps={{ p: 4 }} />
      ) : (
        revisions &&
        (revisions.length === 0 ? (
          <Box p={PageContentPadding}>
            <Alert severity='info'>{t('rawMetadata.empty')}</Alert>
          </Box>
        ) : (
          <Fade appear in>
            <Timeline>
              {revisions.map(revision => (
                <RawMetadataRevision
                  key={revision.revision}
                  revision={revision}
                  revisionId={{
                    hash: revision.hash,
                    playbackOptionId: id,
                    revisionNumber: revision.revision,
                  }}
                />
              ))}
            </Timeline>
          </Fade>
        ))
      )}
    </Stack>
  );
};

export default RawMetadataRevisions;
